'use client';

import React, { useState } from 'react';
import { X, Check, BookmarkPlus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { EditorialCard } from './editorialData';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { Collection } from './CollectionsSidebar';

interface SaveToCollectionModalProps {
  card: EditorialCard;
  collections: Collection[];
  onSave: (collectionId: string) => void;
  onClose: () => void;
}

export default function SaveToCollectionModal({
  card,
  collections,
  onSave,
  onClose,
}: SaveToCollectionModalProps) {
  const [selectedCollection, setSelectedCollection] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const supabase = createClient();

  const handleSave = async () => {
    if (!selectedCollection || !user) return;
    setSaving(true);
    const col = collections.find((c) => c.id === selectedCollection);
    try {
      const { error } = await supabase
        .from('collection_items')
        .insert({
          collection_id: selectedCollection,
          user_id: user.id,
          article_slug: card.slug || card.id,
          article_title: card.title,
          article_category: card.category || '',
          article_image: card.image || '',
        });
      if (error && error.code !== '23505') throw error; // ignore duplicate
      // Also save to saved_articles for cross-feature sync
      await supabase.from('saved_articles').upsert({
        user_id: user.id,
        article_slug: card.slug || card.id,
        article_title: card.title,
      }, { onConflict: 'user_id,article_slug' });

      onSave(selectedCollection);
      toast.success(`Saved to "${col?.name}"`, { description: card.title });
      onClose();
    } catch {
      toast.error('Failed to save. Please try again.');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-obsidian/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md glass-panel border border-muted rounded-sm shadow-luxury-lg animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-muted">
          <div className="flex items-center gap-2">
            <BookmarkPlus size={16} className="text-gold" />
            <span className="font-editorial text-base font-semibold text-cream">
              Save to Collection
            </span>
          </div>
          <button
            onClick={onClose}
            aria-label="Close modal"
            className="text-muted-foreground hover:text-cream transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Card preview */}
        <div className="px-6 py-4 border-b border-muted">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-sm overflow-hidden flex-shrink-0 bg-muted" />
            <div>
              <p className="text-sm font-medium text-cream line-clamp-1">{card.title}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{card.category}</p>
            </div>
          </div>
        </div>

        {/* Collections */}
        <div className="px-6 py-4 space-y-2 max-h-64 overflow-y-auto scroll-hidden">
          <p className="text-xs tracking-widest uppercase text-muted-foreground mb-3">
            Choose a board
          </p>
          {collections.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground">No collections yet.</p>
              <p className="text-xs text-muted-foreground mt-1">Create one from the Collections panel.</p>
            </div>
          ) : (
            collections.map((col) => (
              <button
                key={col.id}
                onClick={() => setSelectedCollection(col.id)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-sm border transition-all duration-200 text-left ${
                  selectedCollection === col.id
                    ? 'border-gold bg-gold/5' :'border-muted hover:border-muted-foreground/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span>{col.emoji}</span>
                  <div>
                    <p className="text-sm text-cream">{col.name}</p>
                    <p className="text-xs text-muted-foreground">{col.item_count} items</p>
                  </div>
                </div>
                {selectedCollection === col.id && (
                  <Check size={14} className="text-gold flex-shrink-0" />
                )}
              </button>
            ))
          )}
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-muted flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border border-muted text-muted-foreground text-sm hover:text-cream transition-colors rounded-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!selectedCollection || saving}
            className="flex-1 py-2.5 gold-gradient-bg text-obsidian text-sm font-semibold tracking-wide rounded-sm disabled:opacity-40 transition-opacity active:scale-[0.98] flex items-center justify-center gap-1.5"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : null}
            Save Discovery
          </button>
        </div>
      </div>
    </div>
  );
}